import {
    ErrorPayload,
    HeartbeatResponse,
    LeaseMetadata,
    LicenseResponse,
    SelectSeatResponse,
    SuccessFlagResponse
} from '../types/license-types';

export const normalizeWindowsDrivePath = (path: string): string =>
    path.replace(/\\/g, '/').replace(/^\/+([A-Za-z]:\/)/, '$1');

export const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

export const isErrorPayload = (value: unknown): value is ErrorPayload => {
    if (!isRecord(value)) return false;
    if ('message' in value && value.message !== undefined && typeof value.message !== 'string') return false;
    if ('code' in value && value.code !== undefined && typeof value.code !== 'string' && typeof value.code !== 'number')
        return false;
    if ('description' in value && value.description !== undefined && typeof value.description !== 'string')
        return false;
    return 'message' in value || 'code' in value || 'description' in value;
};

export const isSuccessFlagResponse = (value: unknown): value is SuccessFlagResponse => {
    if (!isRecord(value) || typeof value.success !== 'boolean') return false;
    const record: Record<string, unknown> = value;
    if ('error' in record) {
        const errorValue = record.error;
        if (!(typeof errorValue === 'string' || isErrorPayload(errorValue))) return false;
    }
    return true;
};

export const isLeaseMetadata = (value: unknown): value is LeaseMetadata => {
    if (!isRecord(value)) return false;
    if (
        'nextHeartbeatInSeconds' in value &&
        value.nextHeartbeatInSeconds !== undefined &&
        typeof value.nextHeartbeatInSeconds !== 'number'
    )
        return false;
    if ('expiresAt' in value && value.expiresAt !== undefined && typeof value.expiresAt !== 'string') return false;
    if ('leaseExpiresAt' in value && value.leaseExpiresAt !== undefined && typeof value.leaseExpiresAt !== 'string')
        return false;
    return true;
};

export const isSelectSeatResponse = (value: unknown): value is SelectSeatResponse => {
    if (!isSuccessFlagResponse(value)) return false;
    const record: Record<string, unknown> = value;
    if ('lease' in record && record.lease !== undefined && !isLeaseMetadata(record.lease)) return false;
    return true;
};

export const isHeartbeatResponse = (value: unknown): value is HeartbeatResponse => isSelectSeatResponse(value);

export const isLicenseResponse = (value: unknown): value is LicenseResponse => {
    if (!isSuccessFlagResponse(value)) return false;
    const record: Record<string, unknown> = value;
    if (record.success) {
        const licenses = record.licenses;
        return Array.isArray(licenses);
    }

    const errorValue: unknown = record.error;
    return isRecord(errorValue);
};

export const toErrorPayload = (error: unknown): ErrorPayload | undefined => {
    if (typeof error === 'string') return { message: error };
    if (!isRecord(error)) return undefined;
    const message: string | undefined = typeof error.message === 'string' ? error.message : undefined;
    const code: string | number | undefined =
        typeof error.code === 'string' || typeof error.code === 'number' ? (error.code as string | number) : undefined;
    const description: string | undefined = typeof error.description === 'string' ? error.description : undefined;
    if (message || code !== undefined || description) return { message, code, description };
    return undefined;
};
